import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store/index";
import { getContractorsByType } from "@/requests/contractors";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { FILTER_NAMES } from "@/static/filterNames";
import { CP_CONTRACTOR_TYPES } from "@/static/cpContractorTypes";
import { Contractor, GetContractorResponse } from "@/models/api/Contractor";
import Vue from "vue";

export interface ContractorsDialogStateInterface {
  dataTableOptions: DataOptions;
  dataTableCriterions: Array<CriterionItemRequest>;

  contractors: Array<Contractor>;
  contractorsTotalCount: number;

  isLoading: boolean;

  isPersisted: Record<string, boolean>;
  selectedContractors: Record<string, Array<Contractor>>;
}

export const ContractorsState: ContractorsDialogStateInterface = {
  dataTableOptions: new DefaultDataOptions(),
  dataTableCriterions: [],

  contractors: [],
  contractorsTotalCount: 0,

  isLoading: false,

  isPersisted: {},
  selectedContractors: {},
};

const ContractorsModule = defineModule({
  state: ContractorsState as ContractorsDialogStateInterface,
  namespaced: true as const,
  getters: {
    isPersisted:
      (...args) =>
      (sourceKey: string) => {
        const { state } = _moduleGetterContext(args);
        return state.isPersisted[sourceKey] ?? false;
      },
    selectedContractors:
      (...args) =>
      (sourceKey: string) => {
        const { state } = _moduleGetterContext(args);
        return state.selectedContractors[sourceKey] ?? [];
      },
  },
  mutations: {
    setContractors(state, response: GetContractorResponse) {
      state.contractors = response.items;
      state.contractorsTotalCount = response.totalCount;
    },
    setIsLoading(state, isLoading: boolean) {
      state.isLoading = isLoading;
    },
    setCriterions(state, criterions: Array<CriterionItemRequest>) {
      state.dataTableCriterions = criterions;
    },
    setDataTableOptions(state, options: DataOptions) {
      state.dataTableOptions = options;
    },
    setSelectedContractors(state, params: { key: string; contractors: Array<Contractor> }) {
      Vue.set(state.selectedContractors, params.key, params.contractors);
    },
    setIsPersisted(state, params: { key: string; isPersisted: boolean }) {
      Vue.set(state.isPersisted, params.key, params.isPersisted);
    },
    clear(state) {
      state.contractors = [];
      state.contractorsTotalCount = 0;
    },
  },
  actions: {
    async getContractors(
      context,
      params: { filterName: FILTER_NAMES; contractorType: CP_CONTRACTOR_TYPES; withoutLocked: boolean }
    ) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getContractorsByType(
        {
          page: state.dataTableOptions.page - 1,
          count: state.dataTableOptions.itemsPerPage,
          criterions: state.dataTableCriterions,
          filterName: params.filterName,
        },
        params.contractorType,
        params.withoutLocked
      );
      commit.setContractors(response);
      commit.setIsLoading(false);
    },
  },
});

export default ContractorsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, ContractorsModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, ContractorsModule);
